import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import { startConversationWithUnion } from '../../utils/startConversationWithUnion';
import { renderJobDescription } from '../../utils/renderDescription';
import LanguageToggle from '../../components/LanguageToggle';
import SiteFooter from '../../components/SiteFooter';
import ApprenticeshipPrerequisites from '../../components/ApprenticeshipPrerequisites';
import ApprenticeshipResourceLink from '../../components/ApprenticeshipResourceLink';
import PortfolioGallery from '../../components/PortfolioGallery';
import VocationWorksLogo from '../../components/VocationWorksLogo';
import LineIcon, { IconTile } from '../../components/LineIcon';

const NAVY       = '#0D1B2A';
const AMBER      = '#F5A623';
const WHITE      = '#FFFFFF';
const GREEN      = '#1D9E75';
const LIGHT_GRAY = '#F7F7F7';
const FONT_HEAD  = "'Bebas Neue', sans-serif";
const FONT_BODY  = "'DM Sans', sans-serif";

// title/desc keys ref unionProfile.* / unionProfile.generic.*, resolved via t() at render time.
const APPLY_STEPS = [
  { num: '1', titleKey: 'unionProfile.meetRequirementsTitle', descKey: 'unionProfile.generic.step1Desc' },
  { num: '2', titleKey: 'unionProfile.applyOnlineTitle',       descKey: 'unionProfile.generic.step2Desc' },
  { num: '3', titleKey: 'unionProfile.startCareerTitle',       descKey: 'unionProfile.generic.step3Desc' },
];

const WHY_VOCATION = [
  { icon: 'target', titleKey: 'unionProfile.whyTitle1', descKey: 'unionProfile.generic.why1Desc' },
  { icon: 'checkCircle', titleKey: 'unionProfile.whyTitle2', descKey: 'unionProfile.generic.why2Desc' },
  { icon: 'briefcase', titleKey: 'unionProfile.whyTitle3', descKey: 'unionProfile.generic.why3Desc' },
];

export default function UnionProfile() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { slug } = useParams();

  const [loading, setLoading]     = useState(true);
  const [notFound, setNotFound]   = useState(false);
  const [unionAccount, setUnionAccount] = useState(null);
  const [payScales, setPayScales] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [benefits, setBenefits]   = useState([]);
  const [apprenticeships, setApprenticeships] = useState([]);
  const [apprExamsMap, setApprExamsMap] = useState({}); // apprenticeship_id -> union_exams rows
  const [expandedApprId, setExpandedApprId] = useState(null);
  const [session, setSession]     = useState(null);
  const [appliedIds, setAppliedIds] = useState(new Set());
  const [applyingId, setApplyingId] = useState(null);
  // apprenticeship id -> { kind: 'error' | 'info', text } shown under that posting's buttons.
  const [applyMsg, setApplyMsg] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setNotFound(false);

      const { data: unionData } = await supabase
        .from('union_accounts')
        .select('*')
        .eq('slug', slug)
        .single();

      if (!unionData) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setUnionAccount(unionData);

      // Everything below keys on union_accounts.id -- the union's own account row is the source
      // of truth for its profile and all of its live content (postings, exams, videos).
      const [payScaleRes, benefitsRes, apprenticeshipsRes, portfolioRes, directoryRes] = await Promise.all([
        supabase.from('union_pay_scales').select('*').eq('union_id', unionData.id).order('sort_order'),
        supabase.from('union_benefits').select('*').eq('union_id', unionData.id).order('sort_order'),
        // Open postings only: active and not archived. An archived posting keeps status
        // 'active', so without the second filter it stayed listed here with a live Interested button.
        supabase.from('apprenticeships').select('*').eq('union_id', unionData.id).eq('status', 'active').is('archived_at', null),
        supabase.from('union_portfolio').select('*').eq('union_id', unionData.id).order('created_at', { ascending: false }),
        // The one thing the link is for: a curated directory entry (`unions`) whose pay scale
        // and benefits predate that union having a login. Only that historical case has one.
        supabase.from('unions').select('id').eq('union_account_id', unionData.id).maybeSingle(),
      ]);
      setPortfolio(portfolioRes.data || []);

      let payScaleRows = payScaleRes.data || [];
      let benefitRows  = benefitsRes.data || [];
      const directoryId = directoryRes.data?.id;
      if (directoryId && (!payScaleRows.length || !benefitRows.length)) {
        const [curatedPay, curatedBenefits] = await Promise.all([
          payScaleRows.length ? Promise.resolve({ data: null }) : supabase.from('union_pay_scales').select('*').eq('union_id', directoryId).order('sort_order'),
          benefitRows.length  ? Promise.resolve({ data: null }) : supabase.from('union_benefits').select('*').eq('union_id', directoryId).order('sort_order'),
        ]);
        payScaleRows = curatedPay.data?.length ? curatedPay.data : payScaleRows;
        benefitRows  = curatedBenefits.data?.length ? curatedBenefits.data : benefitRows;
      }

      const apprRows = apprenticeshipsRes.data || [];
      // union_exams + apprenticeship_exam_requirements are publicly readable, so this works
      // for signed-out visitors too.
      const examsByAppr = {};
      if (apprRows.length) {
        const { data: examLinks } = await supabase
          .from('apprenticeship_exam_requirements')
          .select('apprenticeship_id, union_exams(id, name, description, next_test_date, registration_deadline, location, passing_score, exam_fee, study_guide_url, scheduled_individually, contact_name, contact_title, contact_phone, contact_email, union_exam_sessions(test_date, registration_deadline))')
          .in('apprenticeship_id', apprRows.map(a => a.id));
        (examLinks || []).forEach(link => {
          if (!link.union_exams) return;
          // The embed arrives under the table name; the display helpers read `sessions`.
          const { union_exam_sessions: sessions, ...exam } = link.union_exams;
          (examsByAppr[link.apprenticeship_id] ||= []).push({ ...exam, sessions: sessions || [] });
        });
      }

      setPayScales(payScaleRows);
      setBenefits(benefitRows);
      setApprenticeships(apprRows);
      setApprExamsMap(examsByAppr);
      setLoading(false);
    };
    fetchData();
  }, [slug]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
  }, []);

  useEffect(() => {
    const checkApplied = async () => {
      if (!session || apprenticeships.length === 0) return;
      const ids = apprenticeships.map(a => a.id);
      const { data } = await supabase
        .from('apprenticeship_applications')
        .select('apprenticeship_id')
        .eq('worker_id', session.user.id)
        .in('apprenticeship_id', ids);
      setAppliedIds(new Set((data || []).map(d => d.apprenticeship_id)));
    };
    checkApplied();
  }, [session, apprenticeships]);

  const handleApply = async (appr) => {
    if (!session) { navigate('/worker/signup'); return; }
    if (appliedIds.has(appr.id) || applyingId === appr.id) return;
    setApplyingId(appr.id);
    const showMsg = (kind, key) => setApplyMsg(prev => ({ ...prev, [appr.id]: { kind, text: t(key) } }));
    setApplyMsg(prev => { const { [appr.id]: _, ...rest } = prev; return rest; });
    try {
      // Only worker accounts can apply (the insert policy requires worker_id = auth.uid() of a
      // worker). An employer/union/organization login has no workers row, so say so plainly
      // instead of sending an insert the database will refuse.
      const { data: workerRow, error: workerErr } = await supabase.from('workers').select('id').eq('id', session.user.id).maybeSingle();
      if (workerErr) { showMsg('error', 'unionProfile.applyFailed'); return; }
      if (!workerRow) { showMsg('error', 'unionProfile.notWorkerAccount'); return; }

      // Exactly the four columns the insert policy allows; union_id is the posting's own union.
      const { data: created, error } = await supabase.from('apprenticeship_applications').insert({
        worker_id: session.user.id,
        union_id: appr.union_id,
        apprenticeship_id: appr.id,
        status: 'pending',
      }).select('id');
      if (error?.code === '23505') {
        // unique (apprenticeship_id, worker_id): they applied before, e.g. from their dashboard.
        setAppliedIds(prev => new Set(prev).add(appr.id));
        showMsg('info', 'unionProfile.alreadyApplied');
        return;
      }
      if (error || created?.length !== 1) { showMsg('error', 'unionProfile.applyFailed'); return; }
      setAppliedIds(prev => new Set(prev).add(appr.id));
    } catch (_) {
      showMsg('error', 'unionProfile.applyFailed');
    } finally {
      setApplyingId(null);
    }
  };

  const handleMessageUnion = async (appr) => {
    if (!session) { navigate('/worker/signup'); return; }
    await startConversationWithUnion(supabase, session.user.id, unionAccount.id, appr.id, 'worker');
    navigate('/worker/dashboard');
  };

  const scrollToApprenticeships = () => {
    document.getElementById('open-apprenticeships')?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);

    const style = document.createElement('style');
    style.textContent = `
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      html { scroll-behavior: smooth; }
      body { background-color: ${NAVY}; overflow-x: hidden; }
      /* Tablet / small laptop: the four text links + language toggle + CTA outgrow the bar
         (worst in Spanish), so tighten spacing first, then drop the text links like mobile does. */
      @media (max-width: 1100px) {
        .up-nav             { padding: 0 1.5rem !important; gap: 16px; }
        .up-nav-links       { gap: 1.25rem !important; }
        .up-nav-right       { gap: 1.25rem !important; }
      }
      @media (max-width: 999px) {
        .up-nav-links       { display: none !important; }
      }
      @media (max-width: 768px) {
        .up-nav             { padding: 0 1rem !important; gap: 12px; }
        .up-nav-links       { display: none !important; }
        .up-nav-brand       { gap: 8px !important; }
        .up-nav-lockup      { width: 173px !important; height: 32px !important; }
        .up-nav-right       { gap: 8px !important; }
        .up-nav-cta         { padding: 8px 12px !important; font-size: 0.8rem !important; }
        .up-nav-cta-full    { display: none; }
        .up-nav-cta-short   { display: inline !important; }
        .up-hero-grid       { grid-template-columns: 1fr !important; }
        .up-hero-contact    { width: 100% !important; margin-top: 2rem; }
        .up-hero-ctas       { flex-direction: column !important; align-items: stretch !important; }
        .up-hero-ctas > *   { width: 100% !important; justify-content: center; text-align: center; }
        .up-highlight-grid  { grid-template-columns: 1fr !important; }
        .up-pay-table-wrap  { overflow-x: auto !important; -webkit-overflow-scrolling: touch; }
        .up-pay-table       { min-width: 480px !important; }
        .up-benefits-grid   { grid-template-columns: 1fr !important; }
        .up-apply-steps     { grid-template-columns: 1fr !important; }
        .up-appr-grid       { grid-template-columns: 1fr !important; }
        .up-why-grid        { grid-template-columns: 1fr !important; }
      }
      /* Narrow phones: the right-side group (language toggle + CTA) can't shrink further,
         so the wordmark gives way first, then drops out entirely -- the V mark stays. */
      @media (max-width: 389px) {
        .up-nav-lockup      { width: 151px !important; height: 28px !important; }
        .up-nav-cta         { padding: 8px 10px !important; }
      }
      @media (max-width: 374px) {
        .up-nav-lockup      { display: none; }
        .up-nav-mark        { display: block !important; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  const journeymanRate = payScales.find(p => /journeyman/i.test(p.level))?.hourly_rate;

  if (loading) {
    return <div style={{ minHeight: '100dvh', backgroundColor: NAVY }} />;
  }

  if (notFound) {
    return (
      <div style={{
        minHeight: '100dvh', backgroundColor: NAVY, fontFamily: FONT_BODY,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '2rem', textAlign: 'center',
      }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <VocationWorksLogo variant="reversed" layout="mark" size={48} />
        </div>
        <h1 style={{ fontFamily: FONT_HEAD, fontSize: 'clamp(2rem, 5vw, 3rem)', color: WHITE, letterSpacing: '0.03em', marginBottom: '1rem' }}>
          {t('unionProfile.generic.notFoundTitle')}
        </h1>
        <p style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.6)', marginBottom: '2rem', maxWidth: '440px' }}>
          {t('unionProfile.generic.notFoundBody')}
        </p>
        <button
          onClick={() => navigate('/union')}
          style={{
            backgroundColor: AMBER, color: NAVY, border: 'none', borderRadius: '8px',
            padding: '13px 28px', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', fontFamily: FONT_BODY,
          }}
        >
          {t('unionProfile.generic.backToPartners')}
        </button>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: FONT_BODY, color: WHITE, overflowX: 'hidden' }}>

      {/* ── 1. NAVBAR ── */}
      <nav className="up-nav" style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        height: '64px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', padding: '0 2rem',
        backgroundColor: 'rgba(13,27,42,0.95)',
        backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        borderBottom: `1px solid rgba(245,166,35,0.2)`,
      }}>
        <div
          className="up-nav-brand"
          onClick={() => navigate('/')}
          aria-label="Vocation Works"
          style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', minWidth: 0 }}
        >
          {/* The lockup carries its own wordmark, so the narrowest phones swap to the mark
              alone rather than letting it squeeze the CTA off-screen. */}
          <VocationWorksLogo variant="reversed" size={36} className="up-nav-lockup" />
          <VocationWorksLogo variant="reversed" layout="mark" size={32} className="up-nav-mark" />
        </div>

        <div className="up-nav-right" style={{ display: 'flex', alignItems: 'center', gap: '2rem', flexShrink: 0 }}>
          <div className="up-nav-links" style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
            <span onClick={() => navigate('/trades')} style={{ color: WHITE, fontSize: '14px', fontWeight: 500, fontFamily: FONT_BODY, cursor: 'pointer' }}>
              {t('siteNavbar.forWorkers')}
            </span>
            <span onClick={() => navigate('/employers')} style={{ color: WHITE, fontSize: '14px', fontWeight: 500, fontFamily: FONT_BODY, cursor: 'pointer' }}>
              {t('siteNavbar.forEmployers')}
            </span>
            <span onClick={() => navigate('/union')} style={{ color: WHITE, fontSize: '14px', fontWeight: 500, fontFamily: FONT_BODY, cursor: 'pointer' }}>
              {t('siteNavbar.forUnions')}
            </span>
            <span onClick={() => navigate('/login')} style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' }}>
              {t('siteNavbar.signIn')}
            </span>
          </div>
          <LanguageToggle />
          <button
            className="up-nav-cta"
            onClick={scrollToApprenticeships}
            style={{
              backgroundColor: AMBER, color: NAVY, border: 'none',
              borderRadius: '6px', padding: '9px 18px',
              fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer', fontFamily: FONT_BODY,
              whiteSpace: 'nowrap',
            }}
          >
            <span className="up-nav-cta-full">{t('unionProfile.generic.viewOpenings')}</span>
            <span className="up-nav-cta-short" style={{ display: 'none' }}>{t('unionProfile.generic.viewOpeningsShort')}</span>
          </button>
        </div>
      </nav>

      {/* ── 2. HERO ── */}
      <section style={{ backgroundColor: NAVY, paddingTop: '64px', position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: `
            linear-gradient(rgba(245,166,35,0.045) 1px, transparent 1px),
            linear-gradient(90deg, rgba(245,166,35,0.045) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }} />

        <div
          className="up-hero-grid"
          style={{
            position: 'relative', maxWidth: '1100px', margin: '0 auto',
            padding: '4.5rem 2rem', display: 'grid',
            gridTemplateColumns: '1.3fr 1fr', gap: '3rem', alignItems: 'center',
          }}
        >
          {/* Left column */}
          <div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              backgroundColor: 'rgba(245,166,35,0.12)',
              border: '1px solid rgba(245,166,35,0.35)',
              borderRadius: '999px', padding: '7px 18px',
              fontSize: '0.8rem', fontWeight: 600, color: AMBER,
              marginBottom: '1.5rem', letterSpacing: '0.04em',
            }}>
              <LineIcon name="shieldCheck" size={16} />
              {t('unionProfile.badge')}
            </div>

            <h1 style={{
              fontFamily: FONT_HEAD,
              fontSize: 'clamp(2.25rem, 5.5vw, 3.75rem)',
              color: WHITE, letterSpacing: '0.02em',
              lineHeight: 1.05, marginBottom: '0.75rem',
              textTransform: 'uppercase',
            }}>
              {unionAccount.union_name}
            </h1>

            {unionAccount.local_number && (
              <div style={{
                fontFamily: FONT_HEAD, fontSize: 'clamp(1.25rem, 2.5vw, 1.6rem)',
                color: AMBER, letterSpacing: '0.06em', marginBottom: '1.5rem',
                textTransform: 'uppercase',
              }}>
                {t('unionProfile.generic.localLine', { number: unionAccount.local_number })}{unionAccount.city ? ` — ${unionAccount.city}, ${unionAccount.state || ''}` : ''}
              </div>
            )}

            {unionAccount.about && (
              <p style={{
                fontSize: '1.05rem', color: 'rgba(255,255,255,0.8)',
                lineHeight: 1.7, marginBottom: '1.75rem', maxWidth: '560px',
              }}>
                {unionAccount.about}
              </p>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '2rem' }}>
              {unionAccount.city && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '8px 16px', borderRadius: '999px',
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  color: WHITE, fontSize: '0.85rem', fontWeight: 500,
                }}>
                  <LineIcon name="mapPin" size={16} color={AMBER} />
                  {unionAccount.city}{unionAccount.state ? `, ${unionAccount.state}` : ''}
                </span>
              )}
              {unionAccount.trade && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '8px 16px', borderRadius: '999px',
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  color: WHITE, fontSize: '0.85rem', fontWeight: 500,
                }}>
                  <LineIcon name="hardHat" size={16} color="rgba(255,255,255,0.6)" />
                  {unionAccount.trade}
                </span>
              )}
            </div>

            <div className="up-hero-ctas" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <button
                onClick={scrollToApprenticeships}
                style={{
                  backgroundColor: AMBER, color: NAVY, border: 'none',
                  borderRadius: '8px', padding: '15px 28px',
                  fontWeight: 700, fontSize: '1rem', cursor: 'pointer', fontFamily: FONT_BODY,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {t('unionProfile.applyForApprenticeship')}
              </button>
              {unionAccount.website && (
                <a
                  href={unionAccount.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    backgroundColor: 'transparent', color: WHITE,
                    border: '2px solid rgba(255,255,255,0.35)',
                    borderRadius: '8px', padding: '15px 28px',
                    fontWeight: 600, fontSize: '1rem', fontFamily: FONT_BODY,
                    textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  {t('common.visitWebsite')}
                </a>
              )}
            </div>
          </div>

          {/* Right column — contact card (desktop only) */}
          <div className="up-hero-contact" style={{
            backgroundColor: '#132233',
            border: `1px solid rgba(245,166,35,0.4)`,
            borderRadius: '14px',
            padding: '2rem',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.75rem' }}>
              {unionAccount.phone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.95rem', color: 'rgba(255,255,255,0.85)', overflowWrap: 'anywhere' }}><LineIcon name="phone" color={AMBER} />{unionAccount.phone}</div>
              )}
              {unionAccount.email && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.95rem', color: 'rgba(255,255,255,0.85)', overflowWrap: 'anywhere' }}><LineIcon name="envelope" color={AMBER} />{unionAccount.email}</div>
              )}
              {unionAccount.address && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.95rem', color: 'rgba(255,255,255,0.85)', overflowWrap: 'anywhere' }}><LineIcon name="mapPin" color={AMBER} />{unionAccount.address}</div>
              )}
              {unionAccount.website && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.95rem', color: 'rgba(255,255,255,0.85)', overflowWrap: 'anywhere' }}><LineIcon name="globe" color={AMBER} />{unionAccount.website.replace(/^https?:\/\//, '')}</div>
              )}
              {!unionAccount.phone && !unionAccount.email && !unionAccount.address && !unionAccount.website && (
                <div style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.5)' }}>{t('unionProfile.generic.contactInfoComingSoon')}</div>
              )}
            </div>
            {unionAccount.email && (
              <a
                href={`mailto:${unionAccount.email}`}
                style={{
                  display: 'block', textAlign: 'center',
                  backgroundColor: AMBER, color: NAVY,
                  border: 'none', borderRadius: '8px', padding: '13px',
                  fontWeight: 700, fontSize: '0.95rem', fontFamily: FONT_BODY,
                  textDecoration: 'none',
                }}
              >
                {t('unionProfile.generic.contactButton', { unionName: unionAccount.union_name })}
              </a>
            )}
          </div>
        </div>
      </section>

      {/* ── 3. ABOUT ── */}
      <section style={{ backgroundColor: WHITE, padding: '6rem 2rem' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{
            fontFamily: FONT_HEAD, fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
            color: NAVY, letterSpacing: '0.03em', marginBottom: '1.25rem',
            textTransform: 'uppercase',
          }}>
            {t('unionProfile.generic.aboutTitle', { unionName: unionAccount.union_name })}
          </h2>
          <div style={{ width: '56px', height: '3px', backgroundColor: AMBER, marginBottom: '2rem' }} />

          <p style={{ fontSize: '1rem', color: '#444', lineHeight: 1.8, marginBottom: '3rem' }}>
            {unionAccount.about || t('unionProfile.generic.aboutFallback', { unionName: unionAccount.union_name })}
          </p>

          <div className="up-highlight-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.25rem' }}>
            {[
              { text: t('unionProfile.generic.highlight1'), bg: NAVY, color: WHITE },
              { text: t('unionProfile.generic.highlight2'), bg: AMBER, color: NAVY },
              { text: t('unionProfile.generic.highlight3'), bg: NAVY, color: WHITE },
            ].map((h, i) => (
              <div key={i} style={{
                backgroundColor: h.bg, color: h.color,
                borderRadius: '10px', padding: '1.5rem',
                textAlign: 'center', fontWeight: 700, fontSize: '1.05rem',
              }}>
                {h.text}
              </div>
            ))}
          </div>

          {/* Videos -- union_portfolio, publicly readable. Lives inside About (white) so the
              page's white / light-gray section alternation stays intact. */}
          {portfolio.length > 0 && (
            <div data-public-portfolio style={{ marginTop: '3.5rem' }}>
              <h3 style={{
                fontFamily: FONT_HEAD, fontSize: 'clamp(1.75rem, 4vw, 2.4rem)',
                color: NAVY, letterSpacing: '0.03em', marginBottom: '1.5rem', textTransform: 'uppercase',
              }}>
                {t('portfolio.publicUnionHeading')}
              </h3>
              <PortfolioGallery items={portfolio} variant="light" />
            </div>
          )}
        </div>
      </section>

      {/* ── 4. PAY SCALE ── */}
      <section style={{ backgroundColor: LIGHT_GRAY, padding: '6rem 2rem' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h2 style={{
            fontFamily: FONT_HEAD, fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
            color: NAVY, letterSpacing: '0.03em', textAlign: 'center', marginBottom: '1rem',
          }}>
            {t('unionProfile.payScaleTitle')}
          </h2>
          <p style={{
            fontSize: '0.975rem', color: '#555', lineHeight: 1.7,
            textAlign: 'center', maxWidth: '680px', margin: '0 auto 3rem',
          }}>
            {t('unionProfile.generic.payIntro')}
          </p>

          {payScales.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#888', fontSize: '0.95rem' }}>
              {t('unionProfile.generic.payScaleUnavailable', { unionName: unionAccount.union_name })}
            </p>
          ) : (
            <div className="up-pay-table-wrap">
              <table className="up-pay-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                <thead>
                  <tr>
                    {[t('unionProfile.tableLevel'), t('unionProfile.tableHourlyRate'), t('unionProfile.tableAnnualEstimate')].map(h => (
                      <th key={h} style={{
                        backgroundColor: NAVY, color: WHITE,
                        padding: '14px 20px', textAlign: 'left',
                        fontWeight: 700, fontSize: '0.85rem',
                        letterSpacing: '0.04em', textTransform: 'uppercase',
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payScales.map((p, i) => {
                    const isJourneyman = journeymanRate !== undefined && p.hourly_rate === journeymanRate;
                    const rowBg = isJourneyman ? AMBER : (i % 2 === 0 ? WHITE : LIGHT_GRAY);
                    const textColor = isJourneyman ? NAVY : '#333';
                    return (
                      <tr key={i}>
                        <td style={{ padding: '14px 20px', backgroundColor: rowBg, color: textColor, fontWeight: isJourneyman ? 700 : 500 }}>
                          {p.level}
                        </td>
                        <td style={{ padding: '14px 20px', backgroundColor: rowBg, color: isJourneyman ? NAVY : AMBER, fontWeight: 700 }}>
                          ${Number(p.hourly_rate).toFixed(2)}/hr
                        </td>
                        <td style={{ padding: '14px 20px', backgroundColor: rowBg, color: textColor, fontWeight: isJourneyman ? 700 : 500 }}>
                          ${Math.round(Number(p.hourly_rate) * 2080).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {payScales.length > 0 && (
            <p style={{ fontSize: '0.8rem', color: '#888', marginTop: '1.25rem', textAlign: 'center' }}>
              {t('unionProfile.generic.payDisclaimer', { unionName: unionAccount.union_name })}
            </p>
          )}
        </div>
      </section>

      {/* ── 5. BENEFITS ── */}
      <section style={{ backgroundColor: WHITE, padding: '6rem 2rem' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <h2 style={{
            fontFamily: FONT_HEAD, fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
            color: NAVY, letterSpacing: '0.03em', textAlign: 'center', marginBottom: '1rem',
          }}>
            {t('unionProfile.benefitsTitle')}
          </h2>
          <p style={{
            fontSize: '0.975rem', color: '#555', lineHeight: 1.7,
            textAlign: 'center', maxWidth: '680px', margin: '0 auto 3rem',
          }}>
            {t('unionProfile.generic.benefitsIntro', { unionName: unionAccount.union_name })}
          </p>

          {benefits.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#888', fontSize: '0.95rem' }}>
              {t('unionProfile.generic.benefitsUnavailable', { unionName: unionAccount.union_name })}
            </p>
          ) : (
            <div className="up-benefits-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }}>
              {benefits.map((b, i) => (
                <div key={i} style={{
                  backgroundColor: WHITE,
                  borderRadius: '12px',
                  borderTop: `3px solid ${AMBER}`,
                  boxShadow: '0 2px 16px rgba(13,27,42,0.08)',
                  padding: '2rem',
                }}>
                  <LineIcon name="checkCircle" size={24} color={GREEN} style={{ marginBottom: '1rem' }} />
                  <h3 style={{ fontWeight: 700, fontSize: '1.05rem', color: NAVY, marginBottom: '0.75rem' }}>
                    {b.benefit}
                  </h3>
                  <p style={{ fontSize: '0.9rem', color: '#666', lineHeight: 1.7 }}>
                    {b.description}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── 6. OPEN APPRENTICESHIPS ── */}
      <section id="open-apprenticeships" style={{ backgroundColor: LIGHT_GRAY, padding: '6rem 2rem' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <h2 style={{
            fontFamily: FONT_HEAD, fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
            color: NAVY, letterSpacing: '0.03em', textAlign: 'center', marginBottom: '3rem',
          }}>
            {t('unionProfile.openApprTitle')}
          </h2>

          {apprenticeships.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#888', fontSize: '0.95rem' }}>
              {t('unionProfile.generic.noOpenPrograms', { unionName: unionAccount.union_name })}
            </p>
          ) : (
            <div className="up-appr-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem', alignItems: 'start' /* expanding one card's requirements shouldn't stretch its row-mate */ }}>
              {apprenticeships.map(appr => {
                const applied = appliedIds.has(appr.id);
                const apprExams = apprExamsMap[appr.id] || [];
                const hasPrereqs = apprExams.length > 0 || (appr.requirements || []).some(r => (r || '').trim());
                const isExpanded = expandedApprId === appr.id;
                return (
                  <div key={appr.id} style={{
                    backgroundColor: WHITE,
                    borderRadius: '12px',
                    boxShadow: '0 2px 16px rgba(13,27,42,0.08)',
                    padding: '1.75rem',
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                    <h3 style={{ fontWeight: 700, fontSize: '1.15rem', color: NAVY, marginBottom: '0.6rem' }}>
                      {appr.title}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                      <span style={{ backgroundColor: 'rgba(245,166,35,0.15)', color: AMBER, borderRadius: '999px', fontSize: '11px', fontWeight: 700, padding: '3px 10px' }}>
                        {appr.trade}
                      </span>
                      {appr.gi_bill_eligible && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', backgroundColor: 'rgba(29,158,117,0.15)', color: '#1D9E75', borderRadius: '999px', fontSize: '11px', fontWeight: 700, padding: '3px 10px' }}>
                          <LineIcon name="check" size={16} />
                          {t('marketing.trades.giBillEligible')}
                        </span>
                      )}
                      {appr.drug_screening_required && (
                        <span data-testid="drug-screening-badge" style={{ backgroundColor: 'rgba(13,27,42,0.06)', color: NAVY, border: '1px solid rgba(13,27,42,0.25)', borderRadius: '999px', fontSize: '11px', fontWeight: 700, padding: '3px 10px' }}>
                          {t('marketing.trades.drugScreeningRequired')}
                        </span>
                      )}
                      {apprExams.length > 0 && (
                        <span style={{ backgroundColor: NAVY, color: WHITE, borderRadius: '999px', fontSize: '11px', fontWeight: 700, padding: '3px 10px' }}>
                          {t('exams.worker.examRequiredBadge', { count: apprExams.length })}
                        </span>
                      )}
                      <span style={{ fontSize: '0.8rem', color: '#777' }}>
                        {appr.duration ? t('unionProfile.generic.lengthPrefix', { duration: appr.duration }) : ''}{appr.duration && appr.start_date ? ' · ' : ''}{appr.start_date ? t('marketing.trades.startsOn', { date: appr.start_date }) : ''}
                      </span>
                    </div>
                    {appr.hourly_rate_start && (
                      <p style={{ fontWeight: 700, color: AMBER, fontSize: '0.95rem', marginBottom: '0.75rem' }}>
                        ${appr.hourly_rate_start}{appr.hourly_rate_end ? ` - $${appr.hourly_rate_end}` : ''}/hr
                      </p>
                    )}
                    {appr.description && (
                      <div style={{
                        fontSize: '0.88rem', color: '#666', lineHeight: 1.6, marginBottom: '1.25rem',
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}>
                        {renderJobDescription(appr.description)}
                      </div>
                    )}
                    {hasPrereqs && (
                      <div style={{ marginBottom: '1.25rem' }}>
                        <button
                          onClick={() => setExpandedApprId(isExpanded ? null : appr.id)}
                          aria-expanded={isExpanded}
                          style={{
                            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                            fontFamily: FONT_BODY, fontSize: '0.88rem', fontWeight: 700, color: NAVY,
                            textDecoration: 'underline', textDecorationColor: AMBER, textDecorationThickness: '2px', textUnderlineOffset: '4px',
                          }}
                        >
                          {isExpanded ? t('exams.public.hideRequirements') : t('exams.public.showRequirements')}
                        </button>
                        {isExpanded && (
                          <div style={{ marginTop: '1rem' }}>
                            <ApprenticeshipPrerequisites variant="light" exams={apprExams} requirements={appr.requirements} />
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '10px', marginTop: 'auto', flexWrap: 'wrap', alignItems: 'center' }}>
                      <ApprenticeshipResourceLink apprenticeship={appr} variant="light" />
                      <button
                        onClick={() => handleApply(appr)}
                        disabled={applied || applyingId === appr.id}
                        style={{
                          flex: 1, minWidth: '120px',
                          backgroundColor: applied ? '#1D9E75' : AMBER,
                          color: applied ? WHITE : NAVY,
                          border: 'none', borderRadius: '8px', padding: '11px 16px',
                          fontWeight: 700, fontSize: '0.88rem', fontFamily: FONT_BODY,
                          cursor: applied ? 'default' : 'pointer',
                          opacity: applyingId === appr.id ? 0.7 : 1,
                        }}
                      >
                        {applied ? t('unionProfile.applied') : applyingId === appr.id ? t('unionProfile.applying') : t('unionProfile.generic.interested')}
                      </button>
                      <button
                        onClick={() => handleMessageUnion(appr)}
                        style={{
                          flex: 1, minWidth: '140px',
                          backgroundColor: 'transparent', color: NAVY,
                          border: `2px solid ${NAVY}`, borderRadius: '8px', padding: '11px 16px',
                          fontWeight: 700, fontSize: '0.88rem', fontFamily: FONT_BODY, cursor: 'pointer',
                        }}
                      >
                        {t('unionProfile.messageUnion')}
                      </button>
                    </div>
                    {applyMsg[appr.id] && (
                      <p
                        role={applyMsg[appr.id].kind === 'error' ? 'alert' : 'status'}
                        data-testid="apply-message"
                        data-kind={applyMsg[appr.id].kind}
                        style={{ margin: '10px 0 0', fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.45, overflowWrap: 'anywhere', color: applyMsg[appr.id].kind === 'error' ? '#B91C1C' : '#0F6E50' }}
                      >
                        {applyMsg[appr.id].text}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ── 7. HOW TO JOIN ── */}
      <section style={{ backgroundColor: NAVY, padding: '6rem 2rem' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{
            fontFamily: FONT_HEAD, fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
            color: AMBER, letterSpacing: '0.03em', marginBottom: '1.5rem',
            textTransform: 'uppercase',
          }}>
            {t('unionProfile.generic.howToJoinTitle', { unionName: unionAccount.union_name })}
          </h2>
          <p style={{
            fontSize: '1rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.75,
            maxWidth: '700px', margin: '0 auto 3.5rem',
          }}>
            {unionAccount.how_to_apply || t('unionProfile.generic.howToApplyFallback', { unionName: unionAccount.union_name })}
          </p>

          <div className="up-apply-steps" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2rem', marginBottom: '3rem', textAlign: 'left' }}>
            {APPLY_STEPS.map(step => (
              <div key={step.num}>
                <div style={{
                  width: '44px', height: '44px', borderRadius: '50%',
                  backgroundColor: AMBER, color: NAVY,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: FONT_HEAD, fontSize: '1.3rem', marginBottom: '1rem',
                }}>
                  {step.num}
                </div>
                <h3 style={{ fontWeight: 700, fontSize: '1.05rem', color: WHITE, marginBottom: '0.6rem' }}>
                  {t(step.titleKey)}
                </h3>
                <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.65 }}>
                  {t(step.descKey)}
                </p>
              </div>
            ))}
          </div>

          <button
            onClick={scrollToApprenticeships}
            style={{
              display: 'inline-block',
              backgroundColor: AMBER, color: NAVY,
              border: 'none', borderRadius: '8px', padding: '16px 34px',
              fontWeight: 700, fontSize: '1.05rem', cursor: 'pointer', fontFamily: FONT_BODY,
            }}
          >
            {t('unionProfile.applyForApprenticeship')}
          </button>
        </div>
      </section>

      {/* ── 8. WHY VOCATION WORKS ── */}
      <section style={{ backgroundColor: WHITE, padding: '6rem 2rem' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{
            fontFamily: FONT_HEAD, fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
            color: NAVY, letterSpacing: '0.03em', marginBottom: '3rem',
          }}>
            {t('unionProfile.generic.whyTitle')}
          </h2>

          <div className="up-why-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem', marginBottom: '3rem', textAlign: 'left' }}>
            {WHY_VOCATION.map((w, i) => (
              <div key={i} style={{
                backgroundColor: LIGHT_GRAY,
                borderTop: `3px solid ${AMBER}`,
                borderRadius: '10px',
                padding: '2rem',
              }}>
                <IconTile name={w.icon} style={{ marginBottom: '1rem' }} />
                <h3 style={{ fontWeight: 700, fontSize: '1.05rem', color: NAVY, marginBottom: '0.75rem' }}>
                  {t(w.titleKey)}
                </h3>
                <p style={{ fontSize: '0.9rem', color: '#666', lineHeight: 1.7 }}>
                  {t(w.descKey)}
                </p>
              </div>
            ))}
          </div>

          <button
            onClick={() => navigate('/worker/signup')}
            style={{
              backgroundColor: AMBER, color: NAVY,
              border: 'none', borderRadius: '8px', padding: '16px 34px',
              fontWeight: 700, fontSize: '1.05rem', cursor: 'pointer', fontFamily: FONT_BODY,
            }}
          >
            {t('unionProfile.createFreeProfile')}
          </button>
        </div>
      </section>

      {/* ── 9. FOOTER ── */}
      <SiteFooter />

    </div>
  );
}
